import React, { useState, useMemo, useEffect } from 'react';
import { Search, X, Building2, Box, Briefcase, CalendarRange, User, Truck, Package } from 'lucide-react';
import SearchableSelect from '../shared/SearchableSelect';
import PropTypes from 'prop-types';

const extrairIdReal = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (obj.$oid) return obj.$oid;
    if (obj._id) {
        if (typeof obj._id === 'string') return obj._id;
        if (obj._id.$oid) return obj._id.$oid;
        return obj._id.toString();
    }
    if (obj.id) {
        if (typeof obj.id === 'string') return obj.id;
        if (obj.id.$oid) return obj.id.$oid;
        return obj.id.toString();
    }
    if (obj.unidade) {
        return extrairIdReal(obj.unidade);
    }
    return obj.toString();
};

const getArrayData = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.data && Array.isArray(data.data)) return data.data;
    if (data.data && data.data.data && Array.isArray(data.data.data)) return data.data.data;
    if (data.dias && Array.isArray(data.dias)) return data.dias;
    if (data.data && data.data.dias && Array.isArray(data.data.dias)) return data.data.dias;
    return [];
};

const FiltroAgendamentos = ({ 
    onApplyFilters, 
    loading, 
    clientes, 
    accountUsers,
    recursos, 
    servicos, 
    unidades, 
    initialFilters,
    user,
    agendaOptions,
    userServicos = [] 
}) => {
    
    const initialState = {
        unidadeId: '',
        agendaSelecionada: '',
        clienteId: '',
        profissional: '',
        servico: '',
        recurso: '',
        status: 'Agendado',
        tipoAgendamento: ''
    };
    
    const [tempFilters, setTempFilters] = useState(initialState);
    const [originalUnidadeId, setOriginalUnidadeId] = useState('');

    useEffect(() => {
        if (initialFilters) {
            setTempFilters({
                unidadeId: initialFilters.unidadeId || '',
                agendaSelecionada: initialFilters.agendaSelecionada || '',
                clienteId: initialFilters.clienteId || '',
                profissional: initialFilters.profissional || '',
                servico: initialFilters.servico || '',
                recurso: initialFilters.recurso || '',
                status: initialFilters.status || 'Agendado',
                tipoAgendamento: initialFilters.tipoAgendamento || ''
            });
            if (initialFilters.unidadeId) {
                setOriginalUnidadeId(initialFilters.unidadeId);
            }
        }
    }, [initialFilters]);

    const unidadeOptions = useMemo(() => {
        if (!unidades) return [];
        const unidadesArray = getArrayData(unidades);
        return unidadesArray.map(uni => ({ 
            value: extrairIdReal(uni), 
            label: uni.nome || uni.name || 'Unidade sem nome' 
        }));
    }, [unidades]);

    const clienteOptions = useMemo(() => {
        const arrayClientes = getArrayData(clientes);
        if (arrayClientes.length === 0) return [];
        
        const options = arrayClientes.map(cliente => {
            let id = null;
            let nome = null;
            
            if (cliente && cliente._id) {
                id = extrairIdReal(cliente._id);
                nome = cliente.nome || cliente.name;
            } else if (cliente && cliente.user && cliente.user._id) {
                id = extrairIdReal(cliente.user._id);
                nome = cliente.user.name || cliente.user.nome || cliente.nome;
            } else if (cliente && (cliente.id || cliente.userId)) {
                id = extrairIdReal(cliente.id || cliente.userId);
                nome = cliente.name || cliente.nome;
            }
            
            if (id && nome) {
                return { value: id, label: nome };
            }
            return null;
        }).filter(opt => opt && opt.value);
        
        const uniqueOptions = [];
        const seen = new Set();
        for (const opt of options) {
            if (!seen.has(opt.value)) {
                seen.add(opt.value);
                uniqueOptions.push(opt);
            }
        }
        
        return uniqueOptions;
    }, [clientes]);
    
    const agendaSelectOptions = useMemo(() => {
        if (!agendaOptions || !Array.isArray(agendaOptions)) return [];
        return agendaOptions;
    }, [agendaOptions]);

    const profissionalOptions = useMemo(() => {
        if (!agendaOptions || !Array.isArray(agendaOptions)) return [];
        
        let profs = [];
        agendaOptions.forEach(group => {
            if (group.label === 'Profissionais' || group.label === 'Minha Agenda') {
                if (Array.isArray(group.options)) {
                    profs = profs.concat(group.options.map(opt => ({
                        value: opt.value.replace('prof_', ''),
                        label: opt.label.replace('👤 ', '')
                    })));
                }
            }
        });
        
        const uniqueProfs = [];
        const seen = new Set();
        for (const prof of profs) {
            if (!seen.has(prof.value)) {
                seen.add(prof.value);
                uniqueProfs.push(prof);
            }
        }
        
        return uniqueProfs;
    }, [agendaOptions]);

    const recursoOptions = useMemo(() => {
        let recs = [];
        
        if (agendaOptions && Array.isArray(agendaOptions)) {
            agendaOptions.forEach(group => {
                if (group.label === 'Salas e Recursos') {
                    if (Array.isArray(group.options)) {
                        recs = recs.concat(group.options.map(opt => ({
                            value: opt.value.replace('rec_', ''),
                            label: opt.label.replace('📦 ', '')
                        })));
                    }
                }
            });
        }
        
        if (recursos && getArrayData(recursos).length > 0) {
            const recursosArray = getArrayData(recursos);
            const recursosAdicionais = recursosArray
                .filter(r => {
                    const rId = extrairIdReal(r);
                    return rId && !recs.some(rec => rec.value === rId);
                })
                .map(r => ({
                    value: extrairIdReal(r),
                    label: r.nome || r.name || 'Recurso sem nome'
                }));
            recs = [...recs, ...recursosAdicionais];
        }
        
        const uniqueRecs = [];
        const seen = new Set();
        for (const rec of recs) {
            if (rec.value && !seen.has(rec.value)) {
                seen.add(rec.value);
                uniqueRecs.push(rec);
            }
        }
        
        return uniqueRecs;
    }, [agendaOptions, recursos]);

    const servicoOptions = useMemo(() => {
        const arrayServicos = getArrayData(servicos);
        if (arrayServicos.length === 0) return [];

        const options = arrayServicos
            .filter(s => s.ativo !== false && s.agendavel !== false)
            .map(s => ({ 
                value: extrairIdReal(s), 
                label: s.nome || s.name || 'Serviço sem nome'
            }));
        
        const uniqueServicos = [];
        const seen = new Set();
        for (const serv of options) {
            if (serv.value && !seen.has(serv.value)) {
                seen.add(serv.value);
                uniqueServicos.push(serv);
            }
        }
        
        return uniqueServicos;
    }, [servicos]);
    
    const statusOptions = [
        { value: 'Agendado', label: 'Agendado' },
        { value: '', label: 'Todos os status' },
        { value: 'Pendente', label: 'Pendente' }, 
        { value: 'Confirmado', label: 'Confirmado' }, 
        { value: 'Concluido', label: 'Concluído' },
        { value: 'Cancelado', label: 'Cancelado' },
        { value: 'NoShow', label: 'Não Compareceu' },
    ];

    // ✅ CORREÇÃO: Opções de finalidade com TODOS os tipos incluindo "Todos"
    const tipoAgendamentoOptions = [
        { value: '', label: '📋 Todos os tipos' },
        { value: 'servico', label: '🩺 Serviços' },
        { value: 'recurso_interno', label: '📦 Locação Interna' },
        { value: 'recurso_externo', label: '🚚 Locação Externa' }
    ];

    const handleChange = (name, value) => {
        console.log(`🔧 [FiltroAgendamentos] handleChange: ${name} = ${value}`);
        
        if (name === 'tipoAgendamento') {
            setTempFilters(prev => ({ 
                ...prev, 
                tipoAgendamento: value,
                servico: '',
                recurso: ''
            }));
        } else if (name === 'unidadeId') {
            setTempFilters(prev => ({ 
                ...prev, 
                unidadeId: value, 
                agendaSelecionada: '',
                profissional: '',
                recurso: '', 
                servico: '',
                clienteId: '',
                status: 'Agendado'
            }));
        } else if (name === 'agendaSelecionada') {
            if (value && value.startsWith('prof_')) {
                setTempFilters(prev => ({ 
                    ...prev, 
                    agendaSelecionada: value, 
                    profissional: value.replace('prof_', ''), 
                    recurso: '', 
                    tipoAgendamento: 'servico',
                    status: 'Agendado'
                }));
            } else if (value && value.startsWith('rec_')) {
                const recursoId = value.replace('rec_', '');
                setTempFilters(prev => ({ 
                    ...prev, 
                    agendaSelecionada: value, 
                    recurso: recursoId, 
                    profissional: '', 
                    tipoAgendamento: prev.tipoAgendamento === 'recurso_externo' ? 'recurso_externo' : 'recurso_interno',
                    status: 'Agendado'
                }));
            } else if (value && value.startsWith('uni_')) {
                setTempFilters(prev => ({ 
                    ...prev, 
                    agendaSelecionada: value, 
                    unidadeId: value.replace('uni_', ''), 
                    profissional: '', 
                    recurso: '', 
                    servico: '',
                    tipoAgendamento: 'servico',
                    status: 'Agendado'
                }));
            } else {
                setTempFilters(prev => ({ 
                    ...prev, 
                    agendaSelecionada: '', 
                    profissional: '',
                    recurso: '',
                    servico: ''
                }));
            }
        } else if (name === 'recurso') {
            console.log(`🔧 [FiltroAgendamentos] Recurso diretamente selecionado: ${value}`);
            setTempFilters(prev => ({ ...prev, recurso: value }));
        } else {
            setTempFilters(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = (e) => { 
        e.preventDefault(); 
        
        const cleanFilters = { ...tempFilters };
        
        Object.keys(cleanFilters).forEach(key => {
            if (cleanFilters[key] === '' || cleanFilters[key] === null || cleanFilters[key] === undefined) {
                delete cleanFilters[key];
            }
        });
        
        // ✅ Garantir que recurso seja incluído quando tipoAgendamento for recurso_externo
        if (cleanFilters.tipoAgendamento === 'recurso_externo' && !cleanFilters.recurso && tempFilters.recurso) {
            cleanFilters.recurso = tempFilters.recurso;
        }
        
        if (cleanFilters.tipoAgendamento === 'recurso_interno' && !cleanFilters.recurso && tempFilters.recurso) {
            cleanFilters.recurso = tempFilters.recurso;
        }
        
        if (!cleanFilters.unidadeId && cleanFilters.tipoAgendamento !== 'recurso_externo') {
            cleanFilters.unidadeId = originalUnidadeId;
        }
        
        console.log('🔍 [FiltroAgendamentos] Filtros aplicados (FINAL):', JSON.stringify(cleanFilters));
        
        if (onApplyFilters) onApplyFilters(cleanFilters); 
    };
    
    const handleClear = () => { 
        const defaultFilters = { 
            unidadeId: originalUnidadeId,
            agendaSelecionada: '',
            clienteId: '',
            profissional: '',
            servico: '',
            recurso: '',
            status: 'Agendado',
            tipoAgendamento: ''
        };
        
        setTempFilters(defaultFilters); 
        
        console.log('🔍 [FiltroAgendamentos] Filtros resetados:', JSON.stringify(defaultFilters));
        
        if (onApplyFilters) onApplyFilters(defaultFilters); 
    };

    const isUnidadeDisabled = tempFilters.tipoAgendamento === 'recurso_externo';
    const showServicoFilter = tempFilters.tipoAgendamento === 'servico';
    const showRecursoFilter = tempFilters.tipoAgendamento === 'recurso_interno' || tempFilters.tipoAgendamento === 'recurso_externo';
    const showProfissionalFilter = tempFilters.tipoAgendamento === 'servico';

    return (
        <form onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                
                <div className="form-group">
                    <label className="form-label flex items-center gap-1">
                        <Building2 size={14} /> Unidade
                    </label>
                    <select
                        className="form-select w-full"
                        value={tempFilters.unidadeId || ''}
                        onChange={(e) => handleChange('unidadeId', e.target.value)}
                        disabled={isUnidadeDisabled}
                    >
                        <option value="">Todas as unidades</option>
                        {unidadeOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label className="form-label flex items-center gap-1">
                        <CalendarRange size={14} /> Agenda
                    </label>
                    <select
                        className="form-select w-full"
                        value={tempFilters.agendaSelecionada || ''}
                        onChange={(e) => handleChange('agendaSelecionada', e.target.value)}
                        disabled={!tempFilters.unidadeId || agendaSelectOptions.length === 0}
                    >
                        <option value="">📋 Visualizar todas as agendas</option>
                        {agendaSelectOptions.map((group, idx) => (
                            <optgroup key={idx} label={group.label}>
                                {group.options?.map((opt, optIdx) => (
                                    <option key={optIdx} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label className="form-label flex items-center gap-1">
                        <Briefcase size={14} /> Finalidade
                    </label>
                    <select
                        className="form-select w-full"
                        value={tempFilters.tipoAgendamento || ''}
                        onChange={(e) => handleChange('tipoAgendamento', e.target.value)}
                    >
                        {tipoAgendamentoOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
                
                <div className="form-group">
                    <label className="form-label">Cliente</label>
                    <SearchableSelect 
                        options={clienteOptions} 
                        value={tempFilters.clienteId || ''} 
                        onChange={(value) => handleChange('clienteId', value)} 
                        placeholder="Buscar cliente..."
                        disabled={loading || clienteOptions.length === 0}
                    />
                </div>

                {showProfissionalFilter && (
                    <div className="form-group">
                        <label className="form-label flex items-center gap-1">
                            <User size={14} /> Profissional
                        </label>
                        <SearchableSelect 
                            options={profissionalOptions} 
                            value={tempFilters.profissional || ''} 
                            onChange={(value) => handleChange('profissional', value)} 
                            placeholder="Buscar profissional..."
                        />
                    </div>
                )}

                {showServicoFilter && (
                    <div className="form-group">
                        <label className="form-label flex items-center gap-1">
                            <Briefcase size={14} /> Serviço
                        </label>
                        <SearchableSelect 
                            options={servicoOptions} 
                            value={tempFilters.servico || ''} 
                            onChange={(value) => handleChange('servico', value)} 
                            placeholder="Buscar serviço..."
                        />
                    </div>
                )}
                
                {showRecursoFilter && (
                    <div className="form-group">
                        <label className="form-label flex items-center gap-1">
                            <Box size={14} /> Recurso
                        </label>
                        <SearchableSelect 
                            options={recursoOptions} 
                            value={tempFilters.recurso || ''} 
                            onChange={(value) => handleChange('recurso', value)} 
                            placeholder="Buscar recurso..."
                        />
                    </div>
                )}
                
                <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                        className="form-select w-full"
                        value={tempFilters.status || 'Agendado'}
                        onChange={(e) => handleChange('status', e.target.value)}
                    >
                        {statusOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <button
                    type="button"
                    onClick={handleClear}
                    className="action-button action-button-secondary w-full sm:w-auto justify-center"
                    disabled={loading}
                >
                    <X size={18} className="icon"/> Limpar Filtros
                </button>
                <button
                    type="submit"
                    className="action-button action-button-primary w-full sm:w-auto justify-center"
                    disabled={loading}
                >
                    <Search size={18} className="icon"/> Aplicar Filtros
                </button>
            </div>
        </form>
    );
};

FiltroAgendamentos.propTypes = {
    onApplyFilters: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    clientes: PropTypes.array,
    accountUsers: PropTypes.array,
    recursos: PropTypes.array,
    servicos: PropTypes.array,
    unidades: PropTypes.array,
    initialFilters: PropTypes.object,
    user: PropTypes.object,
    agendaOptions: PropTypes.array,
    userServicos: PropTypes.array
};

export default FiltroAgendamentos;